import { CURRENCY, SOURCE_LABELS, toInputDate } from './reportFormat';

const MONEY_FORMAT = `#,##0.00 "${CURRENCY}"`;
const INT_FORMAT = '#,##0';
const PERCENT_FORMAT = '0.00"%"';

const HEADER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF6B4A2E' } };
const TOTAL_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFAF5EE' } };
const HEADER_FONT = { bold: true, color: { argb: 'FFFFFFFF' } };

// exceljs writes Dates as UTC, so shift the local wall-clock time into UTC or
// Excel would show the previous day for anyone east of Greenwich.
const asExcelDate = (value) => {
  const d = new Date(value);
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes()));
};

/**
 * Adds an RTL sheet with a frozen, styled header row. Each column is
 * { header, key, width, format } where format is an Excel number format.
 */
function addTableSheet(workbook, name, columns, rows) {
  const sheet = workbook.addWorksheet(name, {
    views: [{ rightToLeft: true, state: 'frozen', ySplit: 1 }],
  });
  sheet.columns = columns.map(({ header, key, width = 14, format }) => ({
    header,
    key,
    width,
    style: format ? { numFmt: format } : {},
  }));
  sheet.addRows(rows);

  const header = sheet.getRow(1);
  header.font = HEADER_FONT;
  header.fill = HEADER_FILL;
  header.alignment = { vertical: 'middle', horizontal: 'center' };
  header.height = 22;
  return sheet;
}

function styleTotalRow(row) {
  row.font = { bold: true };
  row.fill = TOTAL_FILL;
}

function buildSummarySheet(workbook, report, { filterLabels, issuedBy, now }) {
  const { summary, bySource } = report;
  const sheet = workbook.addWorksheet('الملخص', { views: [{ rightToLeft: true }] });
  sheet.columns = [{ width: 26 }, { width: 40 }];

  const section = (title) => {
    const row = sheet.addRow([title]);
    row.font = HEADER_FONT;
    row.fill = HEADER_FILL;
    sheet.mergeCells(row.number, 1, row.number, 2);
  };
  const line = (label, value, format) => {
    const row = sheet.addRow([label, value]);
    row.getCell(1).font = { bold: true, color: { argb: 'FF6B7280' } };
    if (format) row.getCell(2).numFmt = format;
    row.getCell(2).alignment = { horizontal: 'right' };
  };

  const title = sheet.addRow(['Havana House — تقرير المبيعات حسب التصنيف']);
  title.font = { bold: true, size: 14, color: { argb: 'FF3B210E' } };
  sheet.mergeCells(title.number, 1, title.number, 2);
  sheet.addRow([]);

  section('معايير التقرير');
  line('من تاريخ', asExcelDate(report.startDate), 'yyyy-mm-dd');
  line('إلى تاريخ', asExcelDate(report.endDate), 'yyyy-mm-dd');
  line('التصنيفات', filterLabels.categories);
  line('البائع', filterLabels.cashier);
  line('المصدر', SOURCE_LABELS[filterLabels.source] || SOURCE_LABELS.all);
  line('تاريخ التصدير', asExcelDate(now), 'yyyy-mm-dd hh:mm');
  line('صدّر بواسطة', issuedBy || '—');
  sheet.addRow([]);

  section('الإجماليات');
  line('عدد العمليات', summary.salesCount, INT_FORMAT);
  line('الوحدات المباعة', summary.unitsSold, INT_FORMAT);
  line('عدد التصنيفات', summary.categoriesCount, INT_FORMAT);
  line('إجمالي المبيعات', summary.revenue, MONEY_FORMAT);
  line('التكلفة التقديرية*', summary.cost, MONEY_FORMAT);
  line('الربح التقديري*', summary.profit, MONEY_FORMAT);
  line('هامش الربح', summary.margin, PERCENT_FORMAT);
  line('متوسط قيمة البيع', summary.avgSaleValue, MONEY_FORMAT);
  sheet.addRow([]);

  section('حسب المصدر');
  line(`${SOURCE_LABELS.pos} — العمليات`, bySource.pos.salesCount, INT_FORMAT);
  line(`${SOURCE_LABELS.pos} — المبيعات`, bySource.pos.revenue, MONEY_FORMAT);
  line(`${SOURCE_LABELS.mondoway} — العمليات`, bySource.mondoway.salesCount, INT_FORMAT);
  line(`${SOURCE_LABELS.mondoway} — المبيعات`, bySource.mondoway.revenue, MONEY_FORMAT);
  sheet.addRow([]);

  const note = sheet.addRow(['* التكلفة والربح تقديريان، محسوبان وفق سعر الشراء الحالي لكل منتج.']);
  note.font = { italic: true, size: 9, color: { argb: 'FF9CA3AF' } };
  sheet.mergeCells(note.number, 1, note.number, 2);
}

function buildCategoriesSheet(workbook, { categories, summary }) {
  const sheet = addTableSheet(
    workbook,
    'التصنيفات',
    [
      { header: 'التصنيف', key: 'name', width: 26 },
      { header: 'عدد المنتجات', key: 'productsCount', format: INT_FORMAT },
      { header: 'العمليات', key: 'salesCount', format: INT_FORMAT },
      { header: 'الكمية', key: 'unitsSold', format: INT_FORMAT },
      { header: 'الإيرادات', key: 'revenue', width: 18, format: MONEY_FORMAT },
      { header: 'التكلفة*', key: 'cost', width: 18, format: MONEY_FORMAT },
      { header: 'الربح*', key: 'profit', width: 18, format: MONEY_FORMAT },
      { header: 'الهامش', key: 'margin', format: PERCENT_FORMAT },
      { header: 'الحصة', key: 'share', format: PERCENT_FORMAT },
    ],
    categories.map((c) => ({ ...c, productsCount: c.products.length }))
  );

  styleTotalRow(
    sheet.addRow({
      name: 'الإجمالي',
      productsCount: categories.reduce((n, c) => n + c.products.length, 0),
      salesCount: summary.salesCount,
      unitsSold: summary.unitsSold,
      revenue: summary.revenue,
      cost: summary.cost,
      profit: summary.profit,
      margin: summary.margin,
      share: categories.length ? 100 : 0,
    })
  );
}

function buildProductsSheet(workbook, { categories, summary }) {
  const rows = categories.flatMap((c) =>
    c.products.map((p) => ({ ...p, category: c.name, barcode: p.barcode || '' }))
  );
  const sheet = addTableSheet(
    workbook,
    'المنتجات',
    [
      { header: 'التصنيف', key: 'category', width: 22 },
      { header: 'المنتج', key: 'name', width: 30 },
      { header: 'الباركود', key: 'barcode', width: 18 },
      { header: 'الكمية', key: 'unitsSold', format: INT_FORMAT },
      { header: 'متوسط السعر', key: 'avgUnitPrice', width: 16, format: MONEY_FORMAT },
      { header: 'الإيرادات', key: 'revenue', width: 18, format: MONEY_FORMAT },
      { header: 'التكلفة*', key: 'cost', width: 18, format: MONEY_FORMAT },
      { header: 'الربح*', key: 'profit', width: 18, format: MONEY_FORMAT },
      { header: 'الهامش', key: 'margin', format: PERCENT_FORMAT },
    ],
    rows
  );
  if (rows.length) sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: rows.length + 1, column: 9 } };

  styleTotalRow(
    sheet.addRow({
      category: 'الإجمالي',
      unitsSold: summary.unitsSold,
      revenue: summary.revenue,
      cost: summary.cost,
      profit: summary.profit,
      margin: summary.margin,
    })
  );
}

function buildDailySheet(workbook, { dailyTrend }) {
  addTableSheet(
    workbook,
    'الاتجاه اليومي',
    [
      { header: 'التاريخ', key: 'date', width: 14, format: 'yyyy-mm-dd' },
      { header: 'العمليات', key: 'salesCount', format: INT_FORMAT },
      { header: 'الإيرادات', key: 'revenue', width: 18, format: MONEY_FORMAT },
      { header: 'الربح*', key: 'profit', width: 18, format: MONEY_FORMAT },
    ],
    // Date keys are already calendar days, so read them as UTC directly.
    dailyTrend.map((d) => ({ ...d, date: new Date(`${d.date}T00:00:00Z`) }))
  );
}

function download(buffer, fileName) {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Builds the category report workbook and returns its .xlsx bytes. */
export async function buildCategoryReportWorkbook(report, { filterLabels, issuedBy, now = new Date() }) {
  // Loaded on demand so exceljs stays out of the main bundle.
  const ExcelJS = await import('exceljs');
  const { Workbook } = ExcelJS.default || ExcelJS;

  const workbook = new Workbook();
  workbook.creator = issuedBy || 'Havana House';
  workbook.created = now;

  buildSummarySheet(workbook, report, { filterLabels, issuedBy, now });
  buildCategoriesSheet(workbook, report);
  buildProductsSheet(workbook, report);
  buildDailySheet(workbook, report);

  return workbook.xlsx.writeBuffer();
}

/** Exports the report currently on screen as an .xlsx download. */
export default async function exportCategoryReport(report, options) {
  const buffer = await buildCategoryReportWorkbook(report, options);
  const range = `${toInputDate(new Date(report.startDate))}_${toInputDate(new Date(report.endDate))}`;
  download(buffer, `HavanaHouse-تقرير-مالي-${range}.xlsx`);
}
